import React, { useRef, useState } from 'react';
import { UploadCloud, FileText, Image as ImageIcon, RefreshCcw, X } from 'lucide-react';
import { toast } from '../../utils/toast';
import { API_URL } from '../../api/api';
import { MEMBER_DOCUMENTS, openMemberDocument } from '../../utils/memberDocuments';

// Fields that are protected KYC/HR documents (streamed via the authenticated
// endpoint, not public static files).
const PROTECTED_DOC_FIELDS = new Set(MEMBER_DOCUMENTS.map((d) => d.field));

// Returns ext (no dot) and a label like "PDF", "JPG".
const fileMeta = (val) => {
  if (!val) return { ext: '', label: '', filename: '' };
  // val is either a string path or a File object.
  const filename =
    typeof val === 'string'
      ? String(val).split('/').pop()
      : val.name || 'file';
  const ext = (filename.split('.').pop() || '').toLowerCase();
  const label = ext ? ext.toUpperCase().slice(0, 4) : 'FILE';
  return { ext, label, filename };
};

const isImage = (ext) =>
  ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'heic'].includes(ext);

const resolveUrl = (path) => {
  if (!path || typeof path !== 'string') return null;
  if (/^https?:\/\//.test(path)) return path;
  return `${API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

/**
 * FileUpload — drag-and-drop dropzone that collapses to a filled chip once
 * a file is set. Reads existing string path from `value` (server-side path)
 * or a fresh `File` from `pendingFile` (uploaded but not saved yet).
 *
 * Props:
 *  - name: form field name
 *  - label: visible label (e.g. "Resume / CV")
 *  - accept: file type filter (e.g. "image/*,.pdf")
 *  - hint: helper text under the dropzone
 *  - value: current persisted path (string) or '' if cleared
 *  - pendingFile: File object selected but not yet uploaded
 *  - onChange(file): called with the File when a new file is selected
 *  - onRemove(): clears the field
 *  - onOpen(): opens the stored file. For a document that is not a column of
 *    its own (a skill certificate, addressed by index) the caller knows how to
 *    fetch it and this component does not — so it hands the opener in.
 *  - readOnly: show the file but allow no replace/remove/drop. Opening the
 *    document still works — read-only is not the same as hidden.
 */
export default function FileUpload({
  name,
  label,
  accept,
  hint,
  value,
  pendingFile,
  onChange,
  onRemove,
  onOpen,
  memberId,
  readOnly = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const current = pendingFile || (value ? value : null);
  const meta = fileMeta(current);
  // A protected document on an existing member streams through the auth
  // endpoint (its static /uploads/team/* URL is blocked). Public files (e.g.
  // profile photos) and unsaved pending files keep the plain URL.
  const isSecureDoc =
    !pendingFile &&
    typeof value === 'string' &&
    value &&
    memberId != null &&
    PROTECTED_DOC_FIELDS.has(name);
  // A caller-supplied opener wins over both routes below: it is the only one
  // that can reach a document the server doesn't address by field name.
  const openProvided =
    !pendingFile && value && typeof onOpen === 'function' ? onOpen : null;
  const url =
    pendingFile || isSecureDoc
      ? null
      : typeof value === 'string' && value
      ? resolveUrl(value)
      : null;

  const openSecure = () =>
    openMemberDocument(memberId, name).catch((err) =>
      toast.error(
        err?.response?.status === 403
          ? 'You do not have access to this document.'
          : err?.response?.status === 404
            ? 'The document file is missing.'
            : 'Could not open the document.',
      ),
    );

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (readOnly) return; // a disabled <button> still receives drop events
    const file = e.dataTransfer?.files?.[0];
    if (file) onChange?.(file);
  };

  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) onChange?.(file);
    // Reset the input so picking the same file again still triggers onChange.
    e.target.value = '';
  };

  const trigger = () => {
    if (readOnly) return;
    inputRef.current?.click();
  };

  return (
    <div className="form-item full">
      {label && <label className="file-upload-label">{label}</label>}

      <input
        ref={inputRef}
        type="file"
        name={name}
        accept={accept}
        onChange={handleSelect}
        style={{ display: 'none' }}
      />

      {current ? (
        // ----- Filled chip -----
        <div className="file-chip">
          <span className={`file-chip-pill ${isImage(meta.ext) ? 'is-img' : ''}`}>
            {isImage(meta.ext) ? (
              <ImageIcon size={14} />
            ) : (
              <FileText size={14} />
            )}
            <span>{meta.label}</span>
          </span>
          <div className="file-chip-body">
            {openProvided || isSecureDoc ? (
              <button
                type="button"
                className="file-chip-name file-chip-name-btn"
                title={`View ${meta.filename}`}
                onClick={openProvided || openSecure}
              >
                {meta.filename}
              </button>
            ) : url ? (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="file-chip-name"
                title={meta.filename}
              >
                {meta.filename}
              </a>
            ) : (
              <span className="file-chip-name" title={meta.filename}>
                {meta.filename}
              </span>
            )}
            <small>{pendingFile ? 'Pending upload' : 'Uploaded'}</small>
          </div>
          {!readOnly && (
            <div className="file-chip-actions">
              <button
                type="button"
                className="file-chip-btn"
                onClick={trigger}
                title="Replace"
              >
                <RefreshCcw size={13} />
                <span>Replace</span>
              </button>
              <button
                type="button"
                className="file-chip-btn danger"
                onClick={onRemove}
                title="Remove"
                aria-label="Remove file"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      ) : readOnly ? (
        // ----- Nothing on file, and this viewer cannot add one -----
        <div className="file-drop is-readonly">
          <div className="file-drop-text">
            <span>No file uploaded</span>
          </div>
        </div>
      ) : (
        // ----- Empty dropzone -----
        <button
          type="button"
          className={`file-drop ${dragOver ? 'is-over' : ''}`}
          onClick={trigger}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <span className="file-drop-icon" aria-hidden="true">
            <UploadCloud size={22} />
          </span>
          <div className="file-drop-text">
            <b>Click to upload</b>
            <span> or drag &amp; drop</span>
          </div>
          {hint && <small className="file-drop-hint">{hint}</small>}
        </button>
      )}
    </div>
  );
}
